// Direct imports
import { bindAnimationSources } from "./animationBinding/bindAnimationSources";
import { mountDrivenAnimations } from "./animationBinding/drivenAnimations";
import { Scene } from "@babylonjs/core/scene";
import { adoptLoadedBake, authoringSrc } from "./restoreBakedSrc";
import { SceneObjectData } from "../scenedata/sceneobjectdata";
import { TraceBehavior } from "../behaviors/tracebehavior";
import { BehaviorData } from "../scenedata/behaviordata";
import { GenericBehavior } from "../behaviors/genericbehavior";
import { getBehaviorConstructor } from "../behaviors/BehaviorRegistry";
import { SceneObject, setApplyComponents } from "../core/SceneObject";
import type { TraceScene, SceneEventSystem } from "../core/TraceScene";
import { ScriptBehavior } from "../scripting/ScriptBehavior";
import { FrameData } from "../scenedata/behaviors/framedata";
import { ResourceManager } from "./ResourceManager";
import { ConfigData } from "../core/configdata";
import { Utility } from "../utils/Utility";
import { IMAGE_TYPE } from "../scenedata/behaviors/imagedata";
import { MODEL_TYPE } from "../scenedata/behaviors/modeldata";
import { VIDEO_TYPE } from "../scenedata/behaviors/videodata";
import { AUDIO_CLIP_TYPE } from "../scenedata/behaviors/audioclipdata";
import { createDefaultData } from "../scenedata/schemas/defaults";
import { Logger } from '../utils/Logger';
import { MODEL_EXTENSIONS, SPLAT_EXTENSIONS, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS, AUDIO_EXTENSIONS } from '../utils/AssetTypes';
import {migrateV1ToV2Interactions, hasV1Behaviors, migrateV1CyclerToV2, hasV1Cycler, migrateV1CrossObjectInteractions, normalizeLegacyVectorFields} from "./V1ToV2Migration";
import { normalizeLegacyBakedSlotScale } from "../behaviors/layout";
import { isBuiltInPrimitive, getPrimitiveType, createPrimitiveWithMaterial } from "../export/BuiltInPrimitives";

// 2025
// Author: Aaron Post

/**
 * The behaviors the "src as template" rule adds for an asset-backed `src`.
 * These carry the node's IDENTITY — what it is, not how it looks — so they are
 * registered even when their asset fails to load. See applyComponents.
 */
const ASSET_PRIMITIVE_TYPES = new Set<string>([MODEL_TYPE, IMAGE_TYPE, VIDEO_TYPE, AUDIO_CLIP_TYPE]);

/**
 * {@link SceneCreation.impliedPrimitiveBehaviorData} for an extension already
 * known — the one to call for a FETCHED file, whose kind comes from the name
 * the fetch RESOLVED to because the `src` it is placed by need not name it: a
 * library reference is `<api>/assets/{guid}/file`.
 *
 * Module-scope, not a static: `SceneCreation` is re-exported from
 * `trace-viewer/behaviors`, and this is the engine's own plumbing.
 * @internal
 */
export function impliedBehaviorDataForExtension(ext: string): BehaviorData[] {
    let type: string | null = null;

    if (MODEL_EXTENSIONS.has(ext) || SPLAT_EXTENSIONS.has(ext)) type = MODEL_TYPE;
    else if (IMAGE_EXTENSIONS.has(ext)) type = IMAGE_TYPE;
    else if (VIDEO_EXTENSIONS.has(ext)) type = VIDEO_TYPE;
    else if (AUDIO_EXTENSIONS.has(ext)) type = AUDIO_CLIP_TYPE;

    if (!type) return [];
    const implied: BehaviorData[] = [createDefaultData(type)];

    // Splat shadows are expensive — disable them by default
    if (SPLAT_EXTENSIONS.has(ext)) {
        implied.push(createDefaultData('Renderer', { castShadows: false }));
    }

    return implied;
}

/**
 * `behaviors` with what `implied` adds — the rule
 * {@link SceneCreation.ensurePrimitiveBehaviorData} applies, on plain data, for
 * a caller that has no SceneObject yet. Nothing is added when the implied
 * asset-owning primitive is already there; otherwise the implied entries whose
 * types are missing are appended. Returns `behaviors` itself when it adds
 * nothing. The primitive is `implied[0]`: that is the order
 * {@link impliedBehaviorDataForExtension} returns.
 * @internal
 */
export function withImpliedBehaviorData(
    behaviors: BehaviorData[] | undefined,
    implied: BehaviorData[],
): BehaviorData[] | undefined {
    if (!implied.length) return behaviors;
    const current = behaviors ?? [];
    if (current.some(b => b['@type'] === implied[0]['@type'])) return behaviors;
    return [...current, ...implied.filter(d => !current.some(b => b['@type'] === d['@type']))];
}

/**
 * {@link SceneCreation.ensurePrimitiveBehaviorData} for an extension already
 * resolved (see {@link impliedBehaviorDataForExtension}).
 * @internal
 */
export function ensureBehaviorDataForExtension(ext: string, sceneObject: SceneObject): void {
    const behaviors = sceneObject._getBehaviorData();
    const merged = withImpliedBehaviorData(behaviors, impliedBehaviorDataForExtension(ext));
    if (merged && merged !== behaviors) sceneObject._setBehaviorData(merged);
}

/**
 * The reverse of {@link ensureBehaviorDataForExtension}: drops the asset
 * primitive from a node that owns no file, such as a reference that resolves
 * to a document.
 * @internal
 */
export function dropAssetPrimitiveData(sceneObject: SceneObject): void {
    const behaviors = sceneObject._getBehaviorData();
    if (!behaviors?.some(b => ASSET_PRIMITIVE_TYPES.has(b['@type']))) return;
    sceneObject._setBehaviorData(behaviors.filter(b => !ASSET_PRIMITIVE_TYPES.has(b['@type'])));
}

export class SceneCreation {
    /**
     * Create a single behavior instance from behavior data.
     * Uses the BehaviorRegistry for lookup. Returns null if behavior
     * already exists (Frame deduplication).
     */
    public static createBehavior(
        sceneObject: SceneObject,
        component: BehaviorData,
    ): TraceBehavior | null {
        const type = component["@type"];
        const sceneName = sceneObject.name;

        // Special case: Frame deduplication
        if (type === "Frame") {
            const existingFrame = sceneObject.getBehavior('Frame');
            if (existingFrame) {
                const allFrames = sceneObject.getBehaviors().filter(b => b.name === 'Frame');
                Logger.log(`[SceneCreation] Frame behavior already exists for ${sceneName} (total Frame behaviors: ${allFrames.length}), updating data instead of creating duplicate`);
                void (existingFrame).commit(component as FrameData);
                (existingFrame).init();
                return null;
            }
            Logger.debug(`[SceneCreation] Creating new Frame behavior for ${sceneName}`);
        }

        // Registry lookup
        const Ctor = getBehaviorConstructor(type);
        if (Ctor) {
            const dataWithDefaults = createDefaultData(type, component);
            return new Ctor(sceneObject, dataWithDefaults);
        }

        // Script registry lookup
        const scriptRegistry = sceneObject.traceScene.scriptRegistry;
        const factory = scriptRegistry?.getFactory(type);
        if (factory || (scriptRegistry?.isScriptType(type) && !scriptRegistry.executionEnabled)) {
            return new ScriptBehavior(sceneObject, component, factory);
        }

        // Unknown type — wrap in GenericBehavior to preserve data for serialization
        return new GenericBehavior(sceneObject, component);
    }

    /**
     * Scene-wide pre-pass: resolve V1 cross-object Trigger→Action references.
     * Operates on raw SceneObjectData before construction when possible.
     * Must be called once before per-object applyComponents.
     */
    public static migrateV1CrossObjectBehaviors(sceneDataList: SceneObjectData[]): void {
        // Build map of objectName → behaviors for any object that has V1 behaviors
        const objectBehaviors = new Map<string, BehaviorData[]>();
        let hasCrossObjectPattern = false;

        for (const data of sceneDataList) {
            if (!data.behaviors || !hasV1Behaviors(data.behaviors)) continue;

            objectBehaviors.set(data.name, data.behaviors);

            // Detect cross-object pattern: Triggers without Actions on the same object
            const hasTriggersOnly = data.behaviors.some(b => b['@type'] === 'Triggers')
                && !data.behaviors.some(b => b['@type'] === 'Actions');
            if (hasTriggersOnly) hasCrossObjectPattern = true;
        }

        if (!hasCrossObjectPattern) return;

        migrateV1CrossObjectInteractions(objectBehaviors);

        // Write back mutated behaviors
        for (const data of sceneDataList) {
            const updated = objectBehaviors.get(data.name);
            if (updated) {
                data.behaviors = updated;
            }
        }
    }

    /**
     * Scene-wide pre-pass variant that operates on already-constructed SceneObjects.
     * Used by loadFromPluginResult where SceneObjects are created before migration.
     * @internal
     */
    public static migrateV1CrossObjectBehaviorsOnObjects(sceneObjects: SceneObject[]): void {
        // Extract data views, run migration, write back
        const dataViews: SceneObjectData[] = sceneObjects.map(so => ({
            name: so.name,
            behaviors: so._getBehaviorData(),
        }));

        SceneCreation.migrateV1CrossObjectBehaviors(dataViews);

        // Write back any mutations
        for (let i = 0; i < sceneObjects.length; i++) {
            const updated = dataViews[i].behaviors;
            if (updated) {
                sceneObjects[i]._setBehaviorData(updated);
            }
        }
    }

    /**
     * Build and load every behavior this object's data declares, then register
     * them.
     *
     * Bracketed by {@link SceneObject._beginBehaviorLoad}: the object is
     * already parented (behaviors need the transform), so for the length of
     * the slowest download it is a live child of the scene that declares
     * behaviors none of which exist yet. `isLoadingBehaviors` is that window,
     * and layout reads it to hold the object's whole container rather than
     * arrange it around something it is not. The last close fires the
     * scene's `onBehaviorsLoaded`, on which layout re-solves the nearest
     * Layout, so the arrangement runs as soon as it is real.
     *
     * The close is in a `finally` and the inner call settles every `load()`
     * rather than racing them, so an asset that 404s closes the window like
     * any other. A fetch that never settles at all is a different question
     * and is not answered here: it is bounded on the waiting side, by
     * `ASSET_PATIENCE_MS` in `Layout._isWaitingOn`.
     */
    public static async applyComponents(sceneObject: SceneObject, sceneName: string): Promise<void> {
        sceneObject._beginBehaviorLoad();
        try {
            await SceneCreation._applyBehaviorComponents(sceneObject, sceneName);
        } finally {
            sceneObject._endBehaviorLoad();
        }
        // Built: what changes from here on reshapes the bake an authoring load replaced.
        adoptLoadedBake(sceneObject, sceneObject.traceScene);
        // TRACE_animation_binding: once the object's instance is mounted —
        // an inline-loaded file, or the Model behavior's load above — bind
        // its sources to it, before any behavior attaches, so an `Animation`
        // finds the bound clips by name. Every (re)build of the components
        // re-binds: a reload re-mounts the instance. This is outside the
        // window above on purpose: it fetches animation sources but adds no
        // geometry, so layout has nothing to wait for.
        if (sceneObject.animationBinding?.sources.length) {
            await bindAnimationSources(sceneObject);
        }
        // Driven animations (`extras.trace.driver`) follow their
        // drivers every frame, after the bound channels that feed them.
        mountDrivenAnimations(sceneObject);
    }

    private static async _applyBehaviorComponents(sceneObject: SceneObject, sceneName: string): Promise<void> {
        // "src as template" — src implies geometry and a corresponding behavior.
        // Built-in primitives (e.g. "builtin:primitive_cube.glb") create meshes
        // directly; asset-backed src adds the appropriate behavior data (Model,
        // Image, Video) so the registry picks it up below.
        //
        // …unless the LOADER already instantiated that file. A glTF 2.1
        // `externalAsset` node is inline-loaded by GltfSceneLoaderPlugin, and
        // synthesising a Model for the same `src` here would load it a second
        // time — two copies of the geometry at one position. The rule is
        // symmetric with the plugin's: it skips the inline load when the
        // document authors an asset-owning behavior, and this skips the
        // synthesis when the plugin loaded. Authored behaviors are untouched
        // either way; only the implied one is suppressed.
        const src = sceneObject.src;
        if (src && isBuiltInPrimitive(src)) {
            // A built-in primitive carries no Model, authored or not. The
            // primitive is built right here, so a Model on it is a second
            // body. One arrives from a save of a bake's first open: the bake's
            // src implied a Model, the save kept it beside the
            // `Provenance.bakedFrom` note, and an authoring reopen swaps the
            // src back to this rig with the Model still in the data. It then
            // fetched the bake beside the primitive (the file's URL is
            // registered under the swapped src), and as a `fit: none` layout
            // provider it would hold the primitive's scale against resizes.
            //
            // Other readers take Model presence to mean "an asset owns this
            // node's size", so an authored Model is dropped too: export
            // rewrites the src to `primitive_<host>.glb`, where a kept `fit`
            // would size the card on reopen; `normalizeLegacyBakedSlotScale`
            // would clear the primitive's scale; editors hide size controls.
            // A later swap to an asset starts from the Model it implies. An
            // undo of the swap brings the old Model back by restoring the
            // object's prior data, not by reassigning the old src.
            const behaviors = sceneObject._getBehaviorData();
            if (behaviors?.some(b => b['@type'] === MODEL_TYPE)) {
                Logger.debug(`[SceneCreation] ${sceneName}: "${src}" is built in — dropping the Model a bake left behind`);
                sceneObject._setBehaviorData(behaviors.filter(b => b['@type'] !== MODEL_TYPE));
            }
            const primitiveType = getPrimitiveType(src);
            if (primitiveType) {
                const mesh = createPrimitiveWithMaterial(primitiveType, sceneObject.babylonScene, sceneObject.name);
                mesh.parent = sceneObject.transform;
            }
        } else if (src && sceneObject._assetInstantiated) {
            Logger.debug(`[SceneCreation] ${sceneName}: loader already instantiated "${src}" — no implied asset behavior`);
        } else if (src) {
            SceneCreation.ensurePrimitiveBehaviorData(src, sceneObject);
        }

        // Normalize legacy {x,y,z} vector fields to [x,y,z] tuples (in place)
        const initialBehaviors = sceneObject._getBehaviorData();
        if (initialBehaviors) {
            for (const b of initialBehaviors) normalizeLegacyVectorFields(b);
        }

        // Pre-provider exports baked the solver's slot scale into Model-leaf
        // TRS; under the provider rules that residue would read as user
        // gizmo scale. Recognize and clear it here, before behaviors
        // construct (see the helper's doc for the match rules).
        normalizeLegacyBakedSlotScale(sceneObject);

        // Migrate V1 Triggers + Actions to BehaviorGraph
        let behaviors = initialBehaviors;
        if (behaviors && hasV1Behaviors(behaviors)) {
            behaviors = migrateV1ToV2Interactions(behaviors, sceneName);
            sceneObject._setBehaviorData(behaviors);
        }

        // Migrate V1 Cycler to Pages behavior
        if (behaviors && hasV1Cycler(behaviors)) {
            behaviors = migrateV1CyclerToV2(behaviors, sceneName);
            sceneObject._setBehaviorData(behaviors);
        }

        if (!behaviors) return;

        Logger.debug(`[SceneCreation] applyComponents(${sceneName}): ${behaviors.length} behaviors — ${behaviors.map(b => b['@type']).join(', ')}`);

        // Load all behaviors in parallel. Asset-producing behaviors (Model,
        // Image, Video) and dependent behaviors (Renderer, Frame, etc.) can
        // safely load concurrently because behaviors only do I/O during load().
        // Mesh-dependent work is deferred to onAttach() which runs after all
        // loads complete (via _attachBehaviors).
        // We use Promise.allSettled + sequential registration to preserve the
        // original behavior order regardless of which loads finish first.
        const entries = behaviors.map(component => ({
            behavior: SceneCreation.createBehavior(sceneObject, component),
            component,
        }));

        // `Promise.resolve(behavior.load())` settles a REJECTED promise, but
        // it never sees a load that throws before it awaits anything (bad
        // authored data read at the top of the method, a missing global): the
        // throw happens while the array is being built, so it escapes
        // allSettled and takes the whole object's build — and the rest of the
        // scene's — with it. Calling through a thenable makes the two failure
        // shapes one.
        const results = await Promise.allSettled(
            entries.map(({ behavior }) =>
                behavior ? Promise.resolve().then(() => behavior.load()) : Promise.resolve()
            )
        );

        for (let i = 0; i < entries.length; i++) {
            const { behavior, component } = entries[i];
            if (!behavior) continue;
            const result = results[i];
            if (result.status === 'fulfilled') {
                sceneObject._registerBehavior(behavior);
            } else {
                Logger.error(`Failed to load resource for ${sceneName} (@type=${component['@type']}):`, result.reason);
                // An asset-backed primitive is what the node IS, not decoration
                // on it — `src` implies it (see the "src as template" rule
                // above), and Model in particular is a scale-preserving
                // LayoutSizeProvider. Dropping it because its fetch failed
                // silently demotes the node to a plain managed leaf, and layout
                // then leaf-scales it to its slot as though the mesh were unit
                // sized. Export bakes that into TRS, and the result is a scene
                // whose geometry is permanently mis-scaled by its own extent
                // once the asset IS reachable — a real-world-scale GLB (an
                // 79 x 163 mm phone) baked against a 1.2 x 2.5 m slot renders
                // at a twelfth of its box.
                //
                // normalizeLegacyBakedSlotScale cannot repair that afterwards:
                // it deliberately requires every axis to equal its predicted
                // slot size, and a cross-axis the solver clamped to its
                // container is no more predictable at load time than the Fill
                // share it already excludes. So keep the identity instead of
                // repairing its absence. A failed load means no MESH, not no
                // behavior: nothing renders either way, and the next load with
                // the asset reachable measures it properly.
                if (ASSET_PRIMITIVE_TYPES.has(component['@type'])) {
                    sceneObject._registerBehavior(behavior);
                }
            }
        }
    }

    /**
     * The behavior data a source path IMPLIES — the asset-owning primitive
     * (Model, Image, Video, AudioClip) the "src as template" rule adds, plus
     * the splat Renderer default. Empty for an unrecognised extension.
     *
     * Split out of {@link ensurePrimitiveBehaviorData} because one caller has
     * no SceneObject to ensure anything on yet. A media LEAF placed by a
     * LIBRARY REFERENCE is built from a placeholder whose `src` is
     * `<api>/assets/{guid}/file` — no extension for the rule to read
     * (spec §3.4) — so what the node IS has to be decided by the name the
     * redirect RESOLVED to, and seeded into the data before construction.
     */
    public static impliedPrimitiveBehaviorData(src: string): BehaviorData[] {
        return impliedBehaviorDataForExtension(Utility.getFileExtensionFromSrc(src));
    }

    /**
     * Ensure that a source path has the corresponding primitive behavior data
     * entry (Model, Image, Video, or AudioClip) in the SceneObject's behaviors.
     * If the entry already exists, does nothing. If unrecognized extension, does nothing.
     */
    public static ensurePrimitiveBehaviorData(src: string, sceneObject: SceneObject): void {
        ensureBehaviorDataForExtension(Utility.getFileExtensionFromSrc(src), sceneObject);
    }

    public static initializeSceneObject(
        sceneData: SceneObjectData,
        scene: Scene,
        resources: ResourceManager,
        config: ConfigData,
        traceScene: TraceScene,
        eventTarget?: SceneEventSystem
    ): SceneObject {
        if(!sceneData.name) {
            sceneData.name = `SceneObject_${crypto.randomUUID()}`;
        }
        // Default active to true if not specified
        if(sceneData.active === undefined) {
            sceneData.active = true;
        }
        // An authoring session opens the RIG, not the bake. Export points a
        // stretched component's `src` at generated plain geometry so a dumb
        // client renders what the editor showed; that asset has no morph
        // targets, so re-opening it gives a lump that can never be re-sized.
        // `Provenance.bakedFrom` names the authored original, and this is the
        // one place to act on it: before the SceneObject exists, so before any
        // fetch is issued — the baked asset is never downloaded, parsed, or
        // disposed, rather than loaded and repaired afterwards.
        //
        // Keyed on the LIVE play state, not the boot config: `config.playMode`
        // comes from an attribute that defaults to true, so an editor which
        // boots without it and calls `setPlayMode(false)` — trace-sandbox —
        // would never satisfy it. See `restoreBakedSrc.ts`.
        sceneData.src = authoringSrc(sceneData.src, sceneData.behaviors, traceScene, sceneData.name, sceneData.uid);
        Logger.debug(`Creating scene object: ${sceneData.name}`);
        const sceneObject = new SceneObject(sceneData, scene, resources, config, traceScene, eventTarget);
        return sceneObject;
    }
}

// Register late-bound reference to break SceneObject ↔ SceneCreation cycle
setApplyComponents((so, name) => SceneCreation.applyComponents(so, name));
